import { motion } from "framer-motion";
import { Target, Users, Lightbulb, Heart } from "lucide-react";

const AboutPage = () => {
  const values = [
    {
      icon: <Target size={32} />,
      title: "Strategy First",
      description: "We believe in understanding before executing. Every campaign starts with research, insights, and clear objectives."
    },
    {
      icon: <Users size={32} />,
      title: "Founder-Led Partnership",
      description: "You work directly with experienced strategists who understand the founder journey and scale challenges."
    },
    {
      icon: <Lightbulb size={32} />,
      title: "AI-Enhanced, Human-Driven",
      description: "We leverage AI for efficiency and insights, but strategic decisions remain grounded in human judgment and creativity."
    },
    {
      icon: <Heart size={32} />,
      title: "Long-Term Growth",
      description: "We optimize for sustainable business outcomes, not vanity metrics or short-term wins."
    }
  ];

  return (
    <div className="min-h-screen noise-texture pt-32">
      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-brandSecondary font-medium text-sm uppercase tracking-wider" data-testid="about-label">
              About Us
            </span>
            <h1 className="font-heading font-bold text-5xl md:text-6xl text-white mt-4 mb-6" data-testid="about-heading">
              Strategic Partners,
              <br />
              <span className="text-gradient">Not Another Agency</span>
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed" data-testid="about-intro">
              Media Points Group was founded on a simple belief: modern marketing shouldn't feel overwhelming. 
              With the right strategy, AI tools, and human expertise, brands can cut through the noise and grow predictably.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Founder Story Section */}
      <section className="py-20 px-6" data-testid="founder-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            {/* Image Left - Asymmetrical */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1764545973653-94c40d993495?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Founder of Media Points Group"
                  className="rounded-3xl shadow-2xl w-full"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-darkBg via-transparent to-transparent rounded-3xl"></div>
              </div>
            </motion.div>

            {/* Content Right */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3 space-y-6"
            >
              <h2 className="font-heading font-bold text-4xl text-white" data-testid="founder-heading">
                Founder-Led. Experience-Driven.
              </h2>
              
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p data-testid="founder-story-p1">
                  After years of working with startups and MSMEs, I noticed a pattern: brands were drowning in 
                  marketing tactics but starving for strategy. They had social media managers, ads running, content 
                  calendars—but no clear thread connecting it all to business goals.
                </p>
                
                <p data-testid="founder-story-p2">
                  Media Points Group was built to solve that. We combine strategic clarity with AI-powered execution 
                  to help ambitious brands grow without the chaos. Every client works directly with me, ensuring 
                  accountability and alignment at every stage.
                </p>
                
                <p data-testid="founder-story-p3">
                  This isn't about vanity metrics or buzzwords. It's about turning data points into touch points, 
                  and touch points into turning points for your business.
                </p>
              </div>

              <div className="glass-card rounded-2xl p-6 border-l-4 border-brandPrimary">
                <p className="text-white italic text-lg" data-testid="founder-quote">
                  "Strategy without execution is hallucination. Execution without strategy is chaos. 
                  We bring both—grounded in AI, led by humans."
                </p>
                <p className="text-gray-400 mt-3 font-medium">— Founder, Media Points Group</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 px-6 bg-darkPaper/50" data-testid="values-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mb-4" data-testid="values-heading">
              What Guides Us
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto" data-testid="values-subheading">
              Principles that shape every partnership and campaign
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                data-testid={`value-card-${idx}`}
                className="glass-card rounded-2xl p-6 hover:border-brandPrimary/50 transition-all"
              >
                <div className="text-brandPrimary mb-4">
                  {value.icon}
                </div>
                <h3 className="font-heading font-semibold text-xl text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 px-6" data-testid="mission-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-3xl p-12 text-center"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6" data-testid="mission-heading">
              Our Mission
            </h2>
            <p className="text-gray-300 text-xl leading-relaxed" data-testid="mission-statement">
              To empower Indian MSMEs, startups, and D2C brands with AI-driven marketing strategies 
              that are clear, actionable, and growth-focused. We exist to turn marketing from a cost 
              center into a predictable growth engine.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
